import { memo, useState, useCallback, useEffect, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";
import { useParams } from "react-router-dom";
import { Inline } from "@pinatagrams/fiesta/components/layout";
import { Title } from "@pinatagrams/fiesta/components/text";
import { Label, Dialog, Button, useOutcome } from "@pinatagrams/fiesta";
import { Dropzone } from "@pinatagrams/fiesta/components";
import { useMessageSizes } from "#/hooks/use-message-sizes";
import { CollectionLabelGroup } from "#/features/collections/components/CollectionLabelGroup";
import type { CollectionItem } from "../../schema";
import type { GraphicSide } from "../state/mutation-queue";
import { useCollectionFormStore } from "../../form-state";
import { useCollectionMutationQueueStore } from "../state/mutation-queue";

interface LabelPreviewProps {
	data: CollectionItem[] | null;
	ordersCount: number;
	onSetGraphicForItem: (input: {
		itemId: string;
		side: GraphicSide;
		file: File;
		previewUrl: string;
	}) => void;
	onSetGraphicForSku: (input: {
		itemId: string;
		sku: string;
		side: GraphicSide;
		file: File;
		previewUrl: string;
	}) => void;
	onSetMessageForItem: (itemId: string, message: string | null) => void;
}

function replaceRecipientTokens(template: string, recipient: CollectionItem["recipient"]): string {
	const recipientValues: Record<string, string> = {
		firstName: recipient.firstName ?? "",
		lastName: recipient.lastName ?? "",
		street1: recipient.street1 ?? "",
		street2: recipient.street2 ?? "",
		city: recipient.city ?? "",
		state: recipient.state ?? "",
		postalCode: recipient.postalCode ?? "",
		country: recipient.country ?? "",
		company: recipient.company ?? "",
	};

	const normalizedRecipientEntries = Object.entries(recipientValues).map(([key, value]) => [
		key.toLowerCase(),
		value,
	]);

	return template.replace(/\{([^}]+)\}/g, (match, rawKey: string) => {
		const normalizedKey = rawKey.trim().toLowerCase();
		const matched = normalizedRecipientEntries.find(([key]) => key === normalizedKey);
		if (!matched) return match;
		return matched[1];
	});
}

/**
 * Preview of collection labels or empty state
 *
 * @param props - Component props
 * @param props.data - Collection items to preview
 * @param props.clientName - Client name for label IDs
 * @param props.collectionName - Collection name for label IDs
 * @param props.collectionId - Collection ID for API requests
 * @param props.ordersCount - Total order count when no data loaded
 *
 * @example
 * ```tsx
 * <LabelPreview
 *   data={data}
 *   clientName={clientName}
 *   collectionName={collectionName}
 *   collectionId={collectionId}
 *   ordersCount={ordersCount}
 * />
 * ```
 */
export const LabelPreview = memo(function LabelPreview({
	data,
	ordersCount,
	onSetGraphicForItem,
	onSetGraphicForSku,
	onSetMessageForItem,
}: LabelPreviewProps) {
	const { id: routeCollectionId } = useParams<{ id: string }>();
	const collectionId = routeCollectionId ?? "draft";

	const { clientName, collectionName } = useCollectionFormStore(
		useShallow((state) => ({
			clientName: state.clientName,
			collectionName: state.collectionName,
		})),
	);

	// Get findDraftGraphicUrl directly from hook
	const findDraftGraphicUrl = useCollectionMutationQueueStore((state) => state.findPreviewUrl);
	const [isGraphicDialogOpen, setIsGraphicDialogOpen] = useState(false);
	const [editingGraphicItemId, setEditingGraphicItemId] = useState<string | null>(null);
	const [isMessageDialogOpen, setIsMessageDialogOpen] = useState(false);
	const [editingMessageItemId, setEditingMessageItemId] = useState<string | null>(null);
	const [editingMessageValue, setEditingMessageValue] = useState("");
	const [outsideBlob, setOutsideBlob] = useState<File | null>(null);
	const [insideBlob, setInsideBlob] = useState<File | null>(null);
	const { emit } = useOutcome();

	const handleGraphicClick = useCallback((itemId: string) => {
		setEditingGraphicItemId(itemId);
		setOutsideBlob(null);
		setInsideBlob(null);
		setIsGraphicDialogOpen(true);
	}, []);

	const handleMessageClick = useCallback((itemId: string, message: string | null) => {
		setEditingMessageItemId(itemId);
		setEditingMessageValue(message ?? "");
		setIsMessageDialogOpen(true);
	}, []);

	const handleGraphicDialogOpenChange = useCallback((open: boolean) => {
		setIsGraphicDialogOpen(open);
		if (!open) {
			setEditingGraphicItemId(null);
			setOutsideBlob(null);
			setInsideBlob(null);
		}
	}, []);

	const handleMessageDialogOpenChange = useCallback((open: boolean) => {
		setIsMessageDialogOpen(open);
		if (!open) {
			setEditingMessageItemId(null);
			setEditingMessageValue("");
		}
	}, []);

	const handleFileDrop = useCallback(
		(type: "outside" | "inside") => (files: File[]) => {
			const file = files[0];
			if (!file) return;

			if (type === "outside") {
				setOutsideBlob(file);
			} else {
				setInsideBlob(file);
			}
		},
		[],
	);

	const handleInvalidFile = useCallback(
		(files: File[] | number) => {
			if (typeof files === "number") {
				emit({
					origin: { id: "label-preview.graphic-edit" },
					impact: "blocking",
					title: "Too many files",
					message: `You can only upload 1 file at a time. You tried to upload ${files}.`,
				});
				return;
			}
			emit({
				origin: { id: "label-preview.graphic-edit" },
				impact: "blocking",
				title: "Invalid file type",
				message: `The following files are not accepted: ${files.map((f) => f.name).join(", ")}. Please upload images or SVG files.`,
			});
		},
		[emit],
	);

	const handleSetForItem = useCallback(() => {
		if (!editingGraphicItemId) return;
		if (!outsideBlob && !insideBlob) return;

		if (outsideBlob) {
			onSetGraphicForItem({
				itemId: editingGraphicItemId,
				side: "outside",
				file: outsideBlob,
				previewUrl: URL.createObjectURL(outsideBlob),
			});
		}

		if (insideBlob) {
			onSetGraphicForItem({
				itemId: editingGraphicItemId,
				side: "inside",
				file: insideBlob,
				previewUrl: URL.createObjectURL(insideBlob),
			});
		}

		handleGraphicDialogOpenChange(false);
		emit({
			origin: { id: "label-preview.graphic-edit" },
			impact: "permissive",
			success: true,
			title: "Graphics staged",
			message: "Graphics were set for this item and will be applied when you save the collection.",
		});
	}, [
		editingGraphicItemId,
		outsideBlob,
		insideBlob,
		handleGraphicDialogOpenChange,
		emit,
		onSetGraphicForItem,
	]);

	const handleSetForSku = useCallback(() => {
		if (!editingGraphicItemId) return;
		if (!outsideBlob && !insideBlob) return;

		const editingItem = data?.find((item) => item.id === editingGraphicItemId);
		const sku = editingItem?.product.sku?.trim();
		if (!sku) {
			emit({
				origin: { id: "label-preview.graphic-edit" },
				impact: "blocking",
				title: "Missing SKU",
				message: "Cannot set for SKU because this item has no SKU.",
			});
			return;
		}

		if (outsideBlob) {
			onSetGraphicForSku({
				itemId: editingGraphicItemId,
				sku,
				side: "outside",
				file: outsideBlob,
				previewUrl: URL.createObjectURL(outsideBlob),
			});
		}

		if (insideBlob) {
			onSetGraphicForSku({
				itemId: editingGraphicItemId,
				sku,
				side: "inside",
				file: insideBlob,
				previewUrl: URL.createObjectURL(insideBlob),
			});
		}

		handleGraphicDialogOpenChange(false);
		emit({
			origin: { id: "label-preview.graphic-edit" },
			impact: "permissive",
			success: true,
			title: "Graphics staged",
			message: `Graphics were set for SKU ${sku} and will be applied when you save the collection.`,
		});
	}, [
		editingGraphicItemId,
		outsideBlob,
		insideBlob,
		data,
		handleGraphicDialogOpenChange,
		emit,
		onSetGraphicForSku,
	]);

	const handleSetMessageForItem = useCallback(() => {
		if (!editingMessageItemId) return;
		const currentEditingItem = data?.find((item) => item.id === editingMessageItemId);
		if (!currentEditingItem) return;
		const nextMessageTemplate = editingMessageValue.trim();
		const resolvedMessage = replaceRecipientTokens(
			nextMessageTemplate,
			currentEditingItem.recipient,
		);
		onSetMessageForItem(editingMessageItemId, resolvedMessage.length > 0 ? resolvedMessage : null);
		handleMessageDialogOpenChange(false);
		emit({
			origin: { id: "label-preview.message-edit" },
			impact: "permissive",
			success: true,
			title: "Message updated",
			message:
				"Message was updated for this item and will be applied when you save the collection.",
		});
	}, [
		editingMessageItemId,
		data,
		editingMessageValue,
		onSetMessageForItem,
		handleMessageDialogOpenChange,
		emit,
	]);

	const handleSetMessageForAll = useCallback(() => {
		if (!data || data.length === 0) return;
		const nextMessageTemplate = editingMessageValue.trim();
		for (const item of data) {
			const resolvedMessage = replaceRecipientTokens(nextMessageTemplate, item.recipient);
			onSetMessageForItem(item.id, resolvedMessage.length > 0 ? resolvedMessage : null);
		}
		handleMessageDialogOpenChange(false);
		emit({
			origin: { id: "label-preview.message-edit" },
			impact: "permissive",
			success: true,
			title: "Messages updated",
			message:
				"Message was updated for all items and will be applied when you save the collection.",
		});
	}, [data, editingMessageValue, onSetMessageForItem, handleMessageDialogOpenChange, emit]);

	const editingItem = data?.find((item) => item.id === editingGraphicItemId);
	const currentOutsideGraphicUrl = editingItem?.graphics?.outside ?? null;
	const currentInsideGraphicUrl = editingItem?.graphics?.inside ?? null;
	const currentDraftOutsideGraphicUrl = editingItem
		? findDraftGraphicUrl(collectionId, editingItem.id, editingItem.product.sku, "outside")
		: null;
	const currentDraftInsideGraphicUrl = editingItem
		? findDraftGraphicUrl(collectionId, editingItem.id, editingItem.product.sku, "inside")
		: null;
	const editingMessageItem = data?.find((item) => item.id === editingMessageItemId);
	const currentMessageValue = editingMessageItem?.message ?? "";
	const hasMessageChange = currentMessageValue !== editingMessageValue.trim();
	const hasBulkMessageChange =
		(editingMessageValue.trim() || currentMessageValue) !== currentMessageValue;
	const currentDraftMessageGraphicUrl = editingMessageItem
		? findDraftGraphicUrl(
				collectionId,
				editingMessageItem.id,
				editingMessageItem.product.sku,
				"inside",
			)
		: null;
	const currentMessageGraphicUrl =
		currentDraftMessageGraphicUrl ?? editingMessageItem?.graphics?.inside;

	const messageSizeInputs = useMemo(() => {
		const inputs = (data ?? []).map((item) => ({
			id: item.id,
			text: item.message ?? "",
		}));

		if (editingMessageItemId) {
			inputs.push({
				id: `${editingMessageItemId}:draft`,
				text: editingMessageValue,
			});
		}

		return inputs;
	}, [data, editingMessageItemId, editingMessageValue]);

	const { getSize: getMessageSize } = useMessageSizes(messageSizeInputs);

	const outsidePreviewUrl = useMemo(() => {
		if (!outsideBlob) return null;
		return URL.createObjectURL(outsideBlob);
	}, [outsideBlob]);
	const insidePreviewUrl = useMemo(() => {
		if (!insideBlob) return null;
		return URL.createObjectURL(insideBlob);
	}, [insideBlob]);

	useEffect(() => {
		if (!outsidePreviewUrl) return;
		return () => {
			URL.revokeObjectURL(outsidePreviewUrl);
		};
	}, [outsidePreviewUrl]);

	useEffect(() => {
		if (!insidePreviewUrl) return;
		return () => {
			URL.revokeObjectURL(insidePreviewUrl);
		};
	}, [insidePreviewUrl]);

	// Compute label items with draft graphics applied
	const labelItems = useMemo(() => {
		if (!data || data.length === 0) return [];

		return data.map((item, index) => ({
			itemKey: item.id || index,
			item,
			index,
			outsideGraphic:
				findDraftGraphicUrl(collectionId, item.id, item.product.sku, "outside") ??
				item.graphics?.outside,
			insideGraphic:
				findDraftGraphicUrl(collectionId, item.id, item.product.sku, "inside") ??
				item.graphics?.inside,
		}));
	}, [collectionId, data, findDraftGraphicUrl]);

	if (data && data.length > 0) {
		return (
			<>
				<Inline wrap gap="md">
					{labelItems.map(({ itemKey, item, index, outsideGraphic, insideGraphic }) => (
						<CollectionLabelGroup
							key={String(itemKey)}
							emoji={item.product.emoji}
							isCustom={item.product.sku.toUpperCase().startsWith("CUSTOM-")}
							productBody={item.product.body}
							filling={item.product.filling}
							additions={item.product.additions}
							addons={item.addons}
							recipientName={`${item.recipient.firstName} ${item.recipient.lastName}`.trim()}
							recipientStreet1={item.recipient.street1}
							message={item.message}
							idNode={
								<Label.Id>
									{clientName?.toUpperCase()}-{collectionName?.toUpperCase()}-{index + 1}
								</Label.Id>
							}
							outsideGraphicUrl={outsideGraphic}
							outsideGraphicAlt={`Front graphic for ${item.product.body}`}
							insideGraphicUrl={insideGraphic}
							insideGraphicAlt={`Message graphic for ${item.product.body}`}
							onOutsideClick={() => {
								handleGraphicClick(item.id);
							}}
							onInsideClick={() => {
								handleMessageClick(item.id, item.message);
							}}
							messageFontSizeCqi={getMessageSize(item.id)}
						/>
					))}
				</Inline>

				<Dialog open={isGraphicDialogOpen} onOpenChange={handleGraphicDialogOpenChange}>
					<Dialog.Content>
						<Dialog.Title>Edit Graphics</Dialog.Title>
						<Dialog.Body>
							<Title hierarchy="subtitle" style={{ marginBottom: "0.5rem" }}>
								Front Graphic
							</Title>
							<Dropzone
								hint="Upload front graphic"
								accept={{
									"image/*": [".png", ".jpg", ".jpeg", ".gif", ".webp"],
									"image/svg+xml": [".svg"],
								}}
								maxFiles={1}
								onFileDrop={handleFileDrop("outside")}
								onInvalidFile={handleInvalidFile}
								size="element"
								aspectRatio="8/4"
							>
								{(outsidePreviewUrl ||
									currentDraftOutsideGraphicUrl ||
									currentOutsideGraphicUrl) && (
									<Label style={{ maxWidth: "none" }}>
										<Label.Graphic
											src={
												outsidePreviewUrl ||
												currentDraftOutsideGraphicUrl ||
												currentOutsideGraphicUrl ||
												""
											}
											alt="Front graphic preview"
										/>
									</Label>
								)}
							</Dropzone>

							<Title hierarchy="subtitle" style={{ marginBottom: "0.5rem", marginTop: "1.5rem" }}>
								Message Graphic
							</Title>
							<Dropzone
								hint="Upload message graphic"
								accept={{
									"image/*": [".png", ".jpg", ".jpeg", ".gif", ".webp"],
									"image/svg+xml": [".svg"],
								}}
								maxFiles={1}
								onFileDrop={handleFileDrop("inside")}
								onInvalidFile={handleInvalidFile}
								size="element"
								aspectRatio="8/4"
							>
								{(insidePreviewUrl || currentDraftInsideGraphicUrl || currentInsideGraphicUrl) && (
									<Label style={{ maxWidth: "none" }}>
										<Label.Graphic
											src={
												insidePreviewUrl ||
												currentDraftInsideGraphicUrl ||
												currentInsideGraphicUrl ||
												""
											}
											alt="Message graphic preview"
										/>
									</Label>
								)}
							</Dropzone>
						</Dialog.Body>
						<Dialog.Footer>
							<Button
								emphasis="bold"
								onClick={() => {
									handleSetForItem();
								}}
								disabled={!outsideBlob && !insideBlob}
							>
								Set for item
							</Button>
							<Button
								emphasis="subtle"
								onClick={() => {
									handleSetForSku();
								}}
								disabled={!outsideBlob && !insideBlob}
							>
								Set for SKU
							</Button>
						</Dialog.Footer>
					</Dialog.Content>
				</Dialog>

				<Dialog open={isMessageDialogOpen} onOpenChange={handleMessageDialogOpenChange}>
					<Dialog.Content>
						<Dialog.Title>Edit Message</Dialog.Title>
						<Dialog.Body>
							<Label style={{ maxWidth: "none" }}>
								{currentMessageGraphicUrl ? (
									<Label.Graphic src={currentMessageGraphicUrl} alt="Message graphic preview" />
								) : (
									<Label.Details>
										<Label.Section variant="message">
											<Label.Name>Message graphic placeholder</Label.Name>
											<Label.Address>Upload message graphic to preview exact layout</Label.Address>
										</Label.Section>
									</Label.Details>
								)}
								<Label.MessageOverlay>
									<Label.MessageText
										placeholder="Enter recipient message"
										value={editingMessageValue}
										onChange={(event) => {
											setEditingMessageValue(event.target.value);
										}}
										style={{
											fontSize: `${getMessageSize(`${editingMessageItemId}:draft`)}cqi`,
										}}
									/>
								</Label.MessageOverlay>
							</Label>
						</Dialog.Body>
						<Dialog.Footer>
							<Button
								emphasis="bold"
								onClick={() => {
									handleSetMessageForItem();
								}}
								disabled={!hasMessageChange}
							>
								Update for item
							</Button>
							<Button
								emphasis="subtle"
								onClick={() => {
									handleSetMessageForAll();
								}}
								disabled={!hasBulkMessageChange}
							>
								Update for all
							</Button>
						</Dialog.Footer>
					</Dialog.Content>
				</Dialog>
			</>
		);
	}

	return (
		<div style={{ marginTop: "2rem" }}>
			<Title hierarchy="subtitle" emphasis="muted">
				{ordersCount} orders in this collection
			</Title>
		</div>
	);
});

LabelPreview.displayName = "LabelPreview";
